import { Suspense, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ImagePlus, Loader2, Sparkles, Wand2 } from 'lucide-react';
import {
  AI_RENDER_REFERENCE_DOCUMENT_TYPE,
  AI_RENDER_DOCUMENT_TYPE,
  FLOOR_PLAN_ATTACHMENT_ENTITY,
  MAX_RENDER_IMAGES,
  MAX_RENDER_PROMPT_LENGTH,
  MAX_RENDER_REFERENCES,
  MAX_RENDER_REFERENCE_BYTES,
  type AiRenderJob,
  type AiRenderStatus,
  type EntityAttachment,
  type ResolvedDecorValues,
} from '@peles/shared';
import { EntityModal, StatusBadge } from '../common';
import { SegmentedControl } from '../common/SegmentedControl';
import { FloorPlan3DView, VIEWPORT_3D } from './FloorPlan3DView';
import type { FloorPlanRealisticHandle } from './FloorPlanRealisticView';
import { LazyRealisticView, hasWebGL } from './realisticViewLoader';
import type { BuildSceneOptions } from './three-d';
import type { CanvasItem } from './types';
import { useCreateFloorPlanRender, useFloorPlanRenders } from '../../hooks/useFloorPlans';
import { useCreateEntityAttachment, useEntityAttachments } from '../../hooks/useEntityAttachments';
import { getRenderUploadUrl } from '../../api/floor-plans';
import { uploadFileToS3 } from '../../api/photos';
import { svgToPng } from '../../utils/svgToPng';

/** Size of the snapshot sent as the layout: the 3D viewport, scaled to the longest side the server draws. */
export const RENDER_SNAPSHOT = {
  width: 1280,
  height: Math.round((1280 * VIEWPORT_3D.height) / VIEWPORT_3D.width),
};

const STATUS_BADGE: Record<AiRenderStatus, { label: string; color: string }> = {
  QUEUED: { label: 'Queued', color: 'gray' },
  RUNNING: { label: 'Drawing…', color: 'blue' },
  DONE: { label: 'Done', color: 'green' },
  FAILED: { label: 'Failed', color: 'red' },
};

const COUNT_OPTIONS = Array.from({ length: MAX_RENDER_IMAGES }, (_, i) => ({
  id: String(i + 1),
  label: String(i + 1),
}));

interface RenderStudioModalProps {
  open: boolean;
  onClose: () => void;
  floorPlanId: string;
  items: CanvasItem[];
  room?: BuildSceneOptions['room'];
  fallbackWidthFt?: number;
  fallbackHeightFt?: number;
  chairSides?: BuildSceneOptions['chairSides'];
  planName?: string;
  /** The event's look, with this plan's changes — what the room is dressed in. */
  decor?: Partial<ResolvedDecorValues>;
  /**
   * Save the canvas before a render. The picture follows the 3D view, which
   * shows the canvas as it is, while the worker describes the plan as saved —
   * rendering over unsaved edits would describe one layout and draw another.
   * Resolves false when the plan could not be saved.
   */
  beforeRender?: () => Promise<boolean>;
}

function isImage(attachment: EntityAttachment) {
  return attachment.mimeType === 'image/png' || attachment.mimeType === 'image/jpeg';
}

type FramingView = 'realistic' | 'diagram';

/**
 * AI Render Studio: a realistic picture of the room as planned.
 *
 * The producer frames the 3D view — that framing is the layout the image model
 * must keep — picks reference photos for the look, and says what they want in
 * their own words. The view framed is the realistic (WebGL) one wherever the
 * browser can draw it: linens, chairs and centrepieces with real depth and
 * shadow give the model far more to follow than the flat diagram, which stays
 * as the fallback (and a choice) where WebGL is missing or fails. A render is a job the server draws in the background; the
 * gallery below asks after it until the pictures arrive, and they are saved
 * among the plan's files.
 */
export function RenderStudioModal({
  open,
  onClose,
  floorPlanId,
  items,
  room,
  fallbackWidthFt,
  fallbackHeightFt,
  chairSides,
  planName,
  decor,
  beforeRender,
}: RenderStudioModalProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const realisticRef = useRef<FloorPlanRealisticHandle>(null);
  // Asked once the studio opens, never by a plan that merely mounts it.
  const webgl = useMemo(() => open && hasWebGL(), [open]);
  const [chosenView, setView] = useState<FramingView | null>(null);
  const view: FramingView = chosenView ?? (webgl ? 'realistic' : 'diagram');
  const [showDecor, setShowDecor] = useState(true);
  const fileInput = useRef<HTMLInputElement>(null);
  const [prompt, setPrompt] = useState('');
  const [count, setCount] = useState('2');
  const [showChairs, setShowChairs] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preparing, setPreparing] = useState(false);

  const renders = useFloorPlanRenders(floorPlanId, open);
  const attachments = useEntityAttachments(FLOOR_PLAN_ATTACHMENT_ENTITY, floorPlanId);
  const createRender = useCreateFloorPlanRender();
  const createAttachment = useCreateEntityAttachment();

  const references = useMemo(
    () =>
      (attachments.data ?? []).filter(
        (a) => isImage(a) && a.documentType !== AI_RENDER_DOCUMENT_TYPE,
      ),
    [attachments.data],
  );
  const usage = renders.data?.usage;
  const jobs = renders.data?.jobs ?? [];
  const spent = usage !== undefined && usage.used >= usage.limit;
  const busy = preparing || createRender.isPending;

  const toggleReference = (id: string) => {
    const photo = references.find((r) => r.id === id);
    if (!selected.includes(id) && (photo?.fileSizeBytes ?? 0) > MAX_RENDER_REFERENCE_BYTES) {
      setError(
        `${photo?.fileName ?? 'That photo'} is larger than ${(MAX_RENDER_REFERENCE_BYTES / 1_000_000).toFixed(1)} MB. Choose a smaller copy.`,
      );
      return;
    }
    setSelected((current) =>
      current.includes(id)
        ? current.filter((x) => x !== id)
        : current.length >= MAX_RENDER_REFERENCES
          ? current
          : [...current, id],
    );
  };

  const uploadReference = async (file: File) => {
    setError(null);
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      setError('Reference photos must be PNG or JPEG.');
      return;
    }
    if (file.size > MAX_RENDER_REFERENCE_BYTES) {
      setError(
        `That photo is larger than ${(MAX_RENDER_REFERENCE_BYTES / 1_000_000).toFixed(1)} MB. Choose a smaller copy.`,
      );
      return;
    }
    setUploading(true);
    try {
      const { uploadUrl, s3Key } = await getRenderUploadUrl(floorPlanId, file.name, file.type);
      await uploadFileToS3(uploadUrl, file);
      const attachment = await createAttachment.mutateAsync({
        entityType: FLOOR_PLAN_ATTACHMENT_ENTITY,
        entityId: floorPlanId,
        fileName: file.name,
        s3Key,
        mimeType: file.type,
        fileSizeBytes: file.size,
        documentType: AI_RENDER_REFERENCE_DOCUMENT_TYPE,
      });
      setSelected((current) =>
        current.length >= MAX_RENDER_REFERENCES ? current : [...current, attachment.id],
      );
    } catch {
      setError('The photo could not be uploaded. Try again.');
    } finally {
      setUploading(false);
    }
  };

  const startRender = async () => {
    setError(null);
    const svg = svgRef.current;
    const realistic = view === 'realistic' ? realisticRef.current : null;
    if (view === 'realistic' ? !realistic : !svg) {
      setError('The 3D view is not ready yet.');
      return;
    }
    setPreparing(true);
    try {
      if (beforeRender && !(await beforeRender())) {
        setError(
          'The plan could not be saved, so the render would not match it. Save it, then render.',
        );
        return;
      }
      const png = realistic
        ? await realistic.captureImage(RENDER_SNAPSHOT.width, RENDER_SNAPSHOT.height)
        : await svgToPng(svg as SVGSVGElement, RENDER_SNAPSHOT);
      if (!png) {
        setError('The 3D view is not ready yet.');
        return;
      }
      const file = new File([png], 'layout.png', { type: 'image/png' });
      const { uploadUrl, s3Key } = await getRenderUploadUrl(floorPlanId, file.name, file.type);
      await uploadFileToS3(uploadUrl, file);
      await createRender.mutateAsync({
        floorPlanId,
        input: {
          prompt: prompt.trim(),
          layoutS3Key: s3Key,
          referenceAttachmentIds: selected,
          count: Number(count),
          width: RENDER_SNAPSHOT.width,
          height: RENDER_SNAPSHOT.height,
        },
      });
    } catch (err) {
      setError(renderErrorMessage(err));
    } finally {
      setPreparing(false);
    }
  };

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={planName ? `AI Render — ${planName}` : 'AI Render'}
      size="xl"
    >
      <div className="space-y-5">
        <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500">
              {webgl && (
                <SegmentedControl<FramingView>
                  ariaLabel="View to frame"
                  value={view}
                  onChange={setView}
                  options={[
                    { id: 'realistic', label: 'Realistic' },
                    { id: 'diagram', label: 'Diagram' },
                  ]}
                />
              )}
              <span className="flex-1 min-w-[12rem]">
                Frame the view to draw — drag to orbit, scroll to zoom.
              </span>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showChairs}
                  onChange={(e) => setShowChairs(e.target.checked)}
                  className="rounded border-gray-300"
                  data-testid="render-toggle-chairs"
                />
                Chairs
              </label>
              {view === 'realistic' && (
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showDecor}
                    onChange={(e) => setShowDecor(e.target.checked)}
                    className="rounded border-gray-300"
                    data-testid="render-toggle-decor"
                  />
                  Centrepieces &amp; place settings
                </label>
              )}
            </div>
            {view === 'realistic' ? (
              <Suspense
                fallback={
                  <div
                    className="aspect-[25/16] w-full rounded-lg bg-gray-100 animate-pulse"
                    data-testid="render-realistic-loading"
                  />
                }
              >
                {/* Daylight and no string lights or table numbers: the model reads
                    the picture's edges as the layout, so it is drawn clear and
                    bare of anything the prompt should decide. */}
                <LazyRealisticView
                  ref={realisticRef}
                  className="aspect-[25/16] w-full"
                  mode="orbit"
                  items={items}
                  room={room}
                  fallbackWidthFt={fallbackWidthFt}
                  fallbackHeightFt={fallbackHeightFt}
                  chairSides={chairSides}
                  showChairs={showChairs}
                  showLabels={false}
                  showStringLights={false}
                  showCenterpieces={showDecor}
                  showPlaceSettings={showDecor}
                  lighting="day"
                  decor={decor}
                  onUnsupported={() => setView('diagram')}
                />
              </Suspense>
            ) : (
              <div className="aspect-[25/16] w-full">
                <FloorPlan3DView
                  svgRef={svgRef}
                  items={items}
                  room={room}
                  fallbackWidthFt={fallbackWidthFt}
                  fallbackHeightFt={fallbackHeightFt}
                  chairSides={chairSides}
                  showChairs={showChairs}
                  showLabels={false}
                  showWalls
                  showGrid={false}
                  perspective
                />
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="render-prompt" className="block text-sm font-medium text-gray-700">
                What should it look like?
              </label>
              <textarea
                id="render-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value.slice(0, MAX_RENDER_PROMPT_LENGTH))}
                rows={4}
                placeholder="Ivory linens, tall white floral centrepieces, warm candlelight, evening"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                data-testid="render-prompt"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Reference photos{' '}
                  <span className="font-normal text-gray-400">
                    ({selected.length}/{MAX_RENDER_REFERENCES})
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  data-testid="render-add-reference"
                >
                  {uploading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ImagePlus size={14} />
                  )}
                  Add photo
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  data-testid="render-reference-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void uploadReference(file);
                  }}
                />
              </div>
              {references.length === 0 ? (
                <p className="mt-1 text-xs text-gray-500">
                  Photos of the hall, the chairs or the flowers make the picture look like them.
                </p>
              ) : (
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {references.map((ref) => {
                    const chosen = selected.includes(ref.id);
                    return (
                      <button
                        key={ref.id}
                        type="button"
                        onClick={() => toggleReference(ref.id)}
                        aria-pressed={chosen}
                        aria-label={ref.fileName ?? 'Reference photo'}
                        className={`aspect-square overflow-hidden rounded-md border-2 ${
                          chosen ? 'border-primary-600' : 'border-transparent opacity-70'
                        }`}
                        data-testid={`render-reference-${ref.id}`}
                      >
                        {ref.downloadUrl ? (
                          <img
                            src={ref.downloadUrl}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="block h-full w-full bg-gray-100" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-700">Pictures</span>
              <SegmentedControl
                options={COUNT_OPTIONS}
                value={count}
                onChange={setCount}
                ariaLabel="Pictures to draw"
              />
            </div>

            {usage && (
              <p className="text-xs text-gray-500" data-testid="render-usage">
                {usage.used} of {usage.limit} renders used for this event this month.
              </p>
            )}
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={() => void startRender()}
              disabled={busy || spent || prompt.trim() === ''}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary-600 px-3 py-2 text-sm text-white hover:bg-primary-700 disabled:opacity-50"
              data-testid="render-start"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {busy ? 'Starting…' : spent ? 'Monthly renders used up' : 'Render'}
            </button>
          </div>
        </div>

        <RenderGallery jobs={jobs} loading={renders.isLoading} />
      </div>
    </EntityModal>
  );
}

function renderErrorMessage(err: unknown): string {
  const response = (err as { response?: { data?: { error?: { message?: string } } } }).response;
  return (
    response?.data?.error?.message ??
    (err instanceof Error && err.message ? err.message : 'The render could not be started.')
  );
}

/** Past and running renders, newest first, with their pictures. */
export function RenderGallery({ jobs, loading }: { jobs: AiRenderJob[]; loading: boolean }) {
  if (loading) {
    return <div className="h-24 animate-pulse rounded-lg bg-gray-100" />;
  }
  if (jobs.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
        No renders yet. Frame the view, describe the look, and press Render.
      </p>
    );
  }
  return (
    <ul className="space-y-4" data-testid="render-gallery">
      {jobs.map((job) => {
        const badge = STATUS_BADGE[job.status];
        return (
          <li key={job.id} className="rounded-lg border border-gray-200 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge label={badge.label} color={badge.color} />
              <span className="min-w-0 flex-1 truncate text-sm text-gray-700" title={job.prompt}>
                {job.prompt}
              </span>
              <span className="text-xs text-gray-400">
                {job.createdBy?.name ? `${job.createdBy.name} · ` : ''}
                {new Date(job.createdAt).toLocaleString()}
              </span>
            </div>
            {job.status === 'FAILED' && job.error && (
              <p className="mt-2 text-sm text-red-600">{job.error}</p>
            )}
            {job.ignored.includes('condition') && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700">
                <AlertTriangle size={14} />
                The configured model could not follow the layout, so this is an impression of the
                look, not the plan.
              </p>
            )}
            {job.ignored.includes('references_unread') && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700">
                <AlertTriangle size={14} />A reference photo could not be read, so this picture was
                drawn without it.
              </p>
            )}
            {(job.status === 'QUEUED' || job.status === 'RUNNING') && (
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {Array.from({ length: job.count }, (_, i) => (
                  <div key={i} className="aspect-[25/16] animate-pulse rounded-md bg-gray-100" />
                ))}
              </div>
            )}
            {job.images.length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {job.images.map((image) => (
                  <figure key={image.attachmentId} className="space-y-1">
                    {image.downloadUrl ? (
                      <a href={image.downloadUrl} target="_blank" rel="noreferrer">
                        <img
                          src={image.downloadUrl}
                          alt={`AI visualisation: ${job.prompt}`}
                          className="aspect-[25/16] w-full rounded-md object-cover"
                        />
                      </a>
                    ) : (
                      <div className="aspect-[25/16] rounded-md bg-gray-100" />
                    )}
                    <figcaption className="text-[11px] text-gray-400">AI visualisation</figcaption>
                  </figure>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Toolbar button that opens the Render Studio. */
export function RenderStudioButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
      data-testid="render-studio-btn"
    >
      <Wand2 size={16} className="text-primary-600" />
      AI Render
    </button>
  );
}
